// Записи справочника: человек и организация, почта, место работы, кураторство (005: data-model.md,
// ADR-0024). Подразделения и общая почта отдела — 006 «Оргструктура». Хозяин записей — человек:
// разбор только предлагает черновики (принципы III, XV).
import { z } from '../kernel/zod.ts';
import { IdSchema, PersonRefSchema, TimestampSchema, VersionSchema } from '../kernel/common.ts';

/** Вид записи справочника; подразделение — 006. */
export const recordKinds = ['person', 'organization'] as const;
export type RecordKind = (typeof recordKinds)[number];
export const RecordKindSchema = z.enum(recordKinds);

export const recordKindLabels = {
  person: 'Человек',
  organization: 'Организация',
} as const satisfies Record<RecordKind, string>;

/**
 * draft — черновик из разбора, confirmed — подтверждён, archived — в архиве, merged — склеена,
 * rekinded — вид сменён: упоминания и псевдонимы у записи другого вида (005-2).
 */
export const recordStates = ['draft', 'confirmed', 'archived', 'merged', 'rekinded'] as const;
export type RecordState = (typeof recordStates)[number];
export const RecordStateSchema = z.enum(recordStates);

/**
 * Подписи состояний. draft — «Не проверено» (правка 0.30.0, слова владельца 02.11.2026): запись,
 * которую создал разбор файла протокола или ввод имени в ячейку таблицы, ещё не проверил человек.
 * Состояние в коде и базе — draft.
 */
export const recordStateLabels = {
  draft: 'Не проверено',
  confirmed: 'Подтверждён',
  archived: 'В архиве',
  merged: 'Склеен',
  rekinded: 'Вид сменён',
} as const satisfies Record<RecordState, string>;

/** own — юрлицо группы (цель области legal_entity 001), external — подрядчик, госорган и т. п. */
export const organizationSides = ['own', 'external'] as const;
export type OrganizationSide = (typeof organizationSides)[number];
export const OrganizationSideSchema = z.enum(organizationSides);

export const organizationSideLabels = {
  own: 'Своя',
  external: 'Внешняя',
} as const satisfies Record<OrganizationSide, string>;

export const externalTypes = ['contractor', 'designer', 'utility', 'authority', 'other'] as const;
export type ExternalType = (typeof externalTypes)[number];
export const ExternalTypeSchema = z.enum(externalTypes);

export const externalTypeLabels = {
  contractor: 'Подрядчик',
  designer: 'Проектировщик',
  utility: 'Ресурсоснабжающая',
  authority: 'Госорган',
  other: 'Другая',
} as const satisfies Record<ExternalType, string>;

/** Ссылка на запись: номер, вид и ФИО или название из текущей записи. */
export const RecordRefSchema = z.object({
  id: IdSchema,
  kind: RecordKindSchema,
  name: z.string(),
});
export type RecordRef = z.infer<typeof RecordRefSchema>;

/**
 * Откуда запись «не проверено» (правка 0.30.0 037): upload — разбор файла протокола при загрузке,
 * table — ввод имени в ячейку таблицы протокола (014 FR-010, FR-011). Номера протокола нет —
 * протокол удалён навсегда.
 */
export const recordOriginSources = ['upload', 'table'] as const;
export const RecordOriginSchema = z.object({
  source: z.enum(recordOriginSources),
  documentNumber: z.number().int().min(1).nullable(),
  at: TimestampSchema,
});
export type RecordOrigin = z.infer<typeof RecordOriginSchema>;

/** Подразделение кратко (006): в месте работы, кураторстве, зоне, упоминании. */
export const UnitRefSchema = z.object({ id: IdSchema, name: z.string() });
export type UnitRef = z.infer<typeof UnitRefSchema>;

export const PERSON_NAME_MAX = 200;
export const ORGANIZATION_NAME_MAX = 300;
export const POSITION_MAX = 200;

export const PersonSchema = z.object({
  id: IdSchema,
  kind: z.literal('person'),
  /** ФИО правится на месте; прежнее остаётся подтверждённым псевдонимом (FR-007а). */
  fullName: z.string(),
  state: RecordStateSchema,
  /** «Не адресат» — записи письма не адресуются (FR-016). */
  notAddressee: z.boolean(),
  /** Главная запись склейки при state = merged. */
  mergedInto: IdSchema.nullable(),
  version: VersionSchema,
});
export type Person = z.infer<typeof PersonSchema>;

export const OrganizationSchema = z.object({
  id: IdSchema,
  kind: z.literal('organization'),
  name: z.string(),
  side: OrganizationSideSchema,
  /** Только у внешней. */
  externalType: ExternalTypeSchema.nullable(),
  /** Только у внешней; по умолчанию false; включает контролёр с подтверждением (FR-017). */
  receivesMail: z.boolean(),
  state: RecordStateSchema,
  notAddressee: z.boolean(),
  mergedInto: IdSchema.nullable(),
  version: VersionSchema,
});
export type Organization = z.infer<typeof OrganizationSchema>;

/**
 * Почта человека (FR-013): внешняя — вне доменов установки; годна для писем после подтверждения
 * или при настройке «Внешняя почта без подтверждения». Видят роли с directory.write (FR-037).
 */
export const EmailSchema = z.object({
  id: IdSchema,
  address: z.string(),
  external: z.boolean(),
  confirmedAt: TimestampSchema.nullable(),
  confirmedBy: PersonRefSchema.nullable(),
  /** У человека одна основная. */
  primary: z.boolean(),
});
export type Email = z.infer<typeof EmailSchema>;

/** Место работы (FR-018): организация и должность; мест несколько, ровно одно основное, дат нет. */
export const WorkplaceSchema = z.object({
  id: IdSchema,
  person: RecordRefSchema,
  organization: RecordRefSchema,
  /** Подразделение организации места работы (006, FR-006); нет — null. */
  unit: UnitRefSchema.nullable(),
  position: z.string().nullable(),
  primary: z.boolean(),
  version: VersionSchema,
});
export type Workplace = z.infer<typeof WorkplaceSchema>;

/** Кураторство (FR-019): наш сотрудник отвечает за организацию; ровно один основной. */
export const CuratorshipSchema = z.object({
  id: IdSchema,
  person: RecordRefSchema,
  organization: RecordRefSchema,
  /** Объект кураторства — подразделение (006, FR-007); null — организация. */
  unit: UnitRefSchema.nullable(),
  primary: z.boolean(),
  version: VersionSchema,
});
export type Curatorship = z.infer<typeof CuratorshipSchema>;

/** Зона ответственности документа (FR-021): юрлицо группы; подразделение — 006. */
export const ZoneSchema = z.object({
  documentId: IdSchema,
  legalEntity: RecordRefSchema.nullable(),
  /** Подразделение юрлица зоны (006, FR-016); нет — null. */
  unit: UnitRefSchema.nullable(),
  setAt: TimestampSchema.nullable(),
  setBy: PersonRefSchema.nullable(),
});
export type Zone = z.infer<typeof ZoneSchema>;
